export interface ServiceArea {
  slug: string;
  name: string;
  state: 'NY' | 'CT';
  region: string;
  description: string;
  intro: string;
  housing: string;
  focus: string[];
}

export const serviceAreas: ServiceArea[] = [
  {
    slug: 'scarsdale',
    name: 'Scarsdale',
    state: 'NY',
    region: 'Westchester County',
    description:
      'Kitchen remodeling, bathroom renovations, and home additions for Scarsdale’s Tudor, colonial, and estate homes. Licensed in New York. Call (914) 960-6433.',
    intro:
      'Scarsdale homeowners usually come to us for kitchens, primary baths, and additions that have to match an older house. Fox Meadow, Heathcote, Greenacres, and Quaker Ridge each have a different mix of Tudors, colonials, and larger estates, and the work has to respect that.',
    housing:
      'Lots are established, ceilings and window lines are often original, and a new kitchen or addition has to line up with the rest of the house. We plan the layout, permits, and finish level before demolition starts.',
    focus: ['Kitchen renovation', 'Primary bathroom remodeling', 'Home additions', 'Interior renovations'],
  },
  {
    slug: 'greenwich',
    name: 'Greenwich',
    state: 'CT',
    region: 'Fairfield County',
    description:
      'Renovations for Greenwich village homes and backcountry estates, including kitchens, baths, additions, and wine cellars. Licensed in Connecticut. Call (914) 960-6433.',
    intro:
      'Greenwich projects range from village houses near downtown to larger backcountry properties. Kitchens, baths, additions, and wine cellars are the jobs we are asked about most on this side of the line.',
    housing:
      'We are licensed in Connecticut as well as New York, so a Greenwich renovation does not have to be handed off at the state border. Finish schedules and site access are planned around the property, not a generic template.',
    focus: ['Luxury kitchens', 'Bathroom remodeling', 'Wine cellars', 'Home additions'],
  },
  {
    slug: 'rye',
    name: 'Rye',
    state: 'NY',
    region: 'Westchester County',
    description:
      'Kitchen and bathroom remodeling in Rye, NY, for village homes and larger properties toward the Sound. Licensed and insured. Call (914) 960-6433.',
    intro:
      'Rye mixes tighter village lots with larger homes closer to the water. Most of the work is kitchens, baths, and interior renovations that have to fit the house that is already there.',
    housing:
      'Village streets leave less room for dumpsters and deliveries, so scheduling and protection of the existing house matter as much as the finishes. We set that up before the crew arrives.',
    focus: ['Kitchen renovation', 'Bathroom remodeling', 'Interior renovations', 'Exterior improvements'],
  },
  {
    slug: 'harrison',
    name: 'Harrison',
    state: 'NY',
    region: 'Westchester County',
    description:
      'Home renovation in Harrison, NY, including kitchens, bathrooms, and additions for colonials and newer houses. Call (914) 960-6433 for a consultation.',
    intro:
      'Harrison covers downtown blocks, West Harrison, and the larger properties toward Purchase. The houses are a mix of older colonials and later construction, so the renovation plan changes with the building.',
    housing:
      'Kitchens and baths are the usual starting point. Additions come up when the family has outgrown the main floor and the lot can take the extra footprint.',
    focus: ['Kitchen renovation', 'Bathroom remodeling', 'Home additions', 'General repairs'],
  },
  {
    slug: 'mamaroneck',
    name: 'Mamaroneck',
    state: 'NY',
    region: 'Westchester County',
    description:
      'Kitchen remodeling, bathroom renovation, and basement work for Mamaroneck and the surrounding Sound Shore. Call (914) 960-6433.',
    intro:
      'Mamaroneck and the harbor neighborhoods have a lot of older homes where the kitchen, baths, and basement are the rooms that no longer work. We remodel those spaces without treating the rest of the house as disposable.',
    housing:
      'Homes near the water need a careful look at the basement and the exterior before a finish plan is locked in. We talk through that on the first visit.',
    focus: ['Kitchen renovation', 'Bathroom remodeling', 'Basement finishing', 'Exterior improvements'],
  },
  {
    slug: 'bedford',
    name: 'Bedford',
    state: 'NY',
    region: 'Westchester County',
    description:
      'Additions and whole-home renovations in Bedford, Bedford Hills, and Katonah. Larger lots, older houses, and room to expand. Call (914) 960-6433.',
    intro:
      'Bedford, Bedford Hills, and Katonah sit on larger lots than the villages closer to the city. Additions, kitchen rebuilds, and whole-floor renovations are the projects that fit these houses.',
    housing:
      'A new wing or a reworked main floor has to meet the existing roofline and the way the family actually uses the house. We measure that before anyone prices a finish.',
    focus: ['Home additions', 'Kitchen renovation', 'Whole-home renovations', 'Custom carpentry'],
  },
  {
    slug: 'chappaqua',
    name: 'Chappaqua',
    state: 'NY',
    region: 'Westchester County',
    description:
      'Kitchen, bathroom, and addition contractors for Chappaqua and New Castle colonials and mid-century homes. Call (914) 960-6433.',
    intro:
      'Chappaqua houses are often colonials or mid-century homes on wooded lots. Families usually want a larger kitchen, another bathroom, or an addition that still feels like part of the original house.',
    housing:
      'Wooded sites and older framing change how an addition is tied in. We look at structure, access, and the existing roof before the design gets ahead of the building.',
    focus: ['Kitchen renovation', 'Home additions', 'Bathroom remodeling', 'Interior renovations'],
  },
  {
    slug: 'bronxville',
    name: 'Bronxville',
    state: 'NY',
    region: 'Westchester County',
    description:
      'Kitchen and bathroom remodeling in Bronxville, NY. Interior renovations for village homes on tight lots. Call (914) 960-6433.',
    intro:
      'Bronxville lots are small and the houses sit close together. The valuable work is inside: kitchens, baths, and room-by-room renovations that add function without pretending the lot can take a big addition.',
    housing:
      'Stairs, ceiling heights, and shared walls decide what is realistic. We plan the renovation around those limits so the result still feels like a Bronxville house.',
    focus: ['Kitchen renovation', 'Bathroom remodeling', 'Interior renovations', 'Custom carpentry'],
  },
  {
    slug: 'larchmont',
    name: 'Larchmont',
    state: 'NY',
    region: 'Westchester County',
    description:
      'Home renovation in Larchmont, NY, for Manor and waterfront colonials. Kitchens, baths, and exterior work. Call (914) 960-6433.',
    intro:
      'Larchmont’s Manor and the streets closer to the water are full of colonials that need updated kitchens, baths, and exteriors. The houses have character worth keeping.',
    housing:
      'We match new work to the existing trim, windows, and exterior so a renovation does not look pasted on. That is usually what Larchmont clients ask for first.',
    focus: ['Kitchen renovation', 'Bathroom remodeling', 'Exterior improvements', 'Interior renovations'],
  },
  {
    slug: 'purchase',
    name: 'Purchase',
    state: 'NY',
    region: 'Westchester County',
    description:
      'Kitchen renovations, bathroom remodeling, and additions for Purchase estates and larger family homes. Call (914) 960-6433.',
    intro:
      'Purchase properties tend to be larger, and the projects follow that: full kitchens, primary suites, and additions rather than a single-room refresh.',
    housing:
      'These houses can carry a higher finish level, but the sequence still matters. We lock the layout and the mechanical work before cabinetry and stone are ordered.',
    focus: ['Luxury kitchens', 'Primary suites', 'Home additions', 'Wine cellars'],
  },
];

export function getServiceArea(slug: string | undefined) {
  if (!slug) return undefined;
  return serviceAreas.find((area) => area.slug === slug);
}
